import { cn } from "@/utils/cnHelper";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

/*
 * @example
 * ```
 * import { Button } from "@ui";
 *
 * <Button variant="primary" size="lg">Button</Button>
 * ```
 */

const buttonVariants = cva(
  "tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-font-CircularXXSub tw-transition-[background-color] disabled:tw-pointer-events-none disabled:tw-opacity-50 tw-outline-none focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-brand_scale-500",
  {
    variants: {
      variant: {
        primary: "tw-bg-brand_scale-500 tw-text-white hover:tw-bg-brand_scale-600",
        "primary-outline":
          "tw-border tw-border-brand_scale-500 tw-bg-transparent tw-text-brand_scale-500 hover:tw-bg-brand_scale-500/10",
        "primary-gradient":
          "tw-bg-primary-gradient tw-bg-[length:100%_auto] tw-text-white hover:tw-bg-[length:200%_auto] tw-transition-[background-size] tw-duration-500 hover:tw-text-grey_scale-100",
        destructive: "tw-bg-error-500 tw-text-white hover:tw-bg-error-500/50",
        "destructive-outline": "tw-border tw-text-error-500 tw-border-error-500 tw-bg-transparent  hover:tw-bg-error-500/10",
        secondary: "tw-bg-white tw-text-black hover:tw-bg-black hover:tw-text-white",
        "secondary-outline": "tw-bg-black tw-text-white tw-border tw-border-white hover:tw-bg-white hover:tw-text-black",
        tertiary: "tw-bg-transparent tw-border tw-border-black tw-text-white hover:tw-bg-white hover:tw-text-black",
        "tertiary-outline": "tw-bg-transparent tw-border tw-border-white tw-text-white hover:tw-bg-grey_scale-700 hover:tw-text-white",
        link: "tw-text-brand_scale-500 tw-underline-offset-4 hover:tw-underline",
      },
      size: {
        sm: "tw-rounded-md tw-px-2 tw-py-2 tw-text-xs tw-font-bold tw-leading-none",
        md: "tw-rounded-xl tw-px-4 tw-py-3 tw-text-base tw-font-semibold tw-leading-none",
        lg: "tw-rounded-2xl tw-px-11 tw-py-5 tw-text-lg tw-font-bold tw-leading-none",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "sm",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
